/**
 * @author      Antigravity AI
 * @date        June, 2026
 * @copyright   www.ibirdsservices.com
 */

import React, { useEffect, useState } from "react";
import {
  Button,
  Col,
  Container,
  Card,
  Row,
  Table,
  Badge,
} from "react-bootstrap";
import {
  DatatableWrapper,
  Filter,
  Pagination,
  PaginationOptions,
  TableBody,
  TableHeader,
} from "react-bs-datatable";
import WhatsAppAPI from "../../api/WhatsAppAPI";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import InstagramSettingEdit from "./InstagramSettingEdit";
import jwt_decode from "jwt-decode";
import { useNavigate } from "react-router-dom";
import { ShimmerTable } from "../common/ShimmerTable";
import { FaTrash, FaEdit, FaPlus } from "react-icons/fa";

const InstagramSetting = () => {
  const navigate = useNavigate();
  const [body, setBody] = useState([]);
  const [isSpinner, setIsSpinner] = useState(false);
  const [modalShowHide, setModalShowHide] = useState(false);
  const [rowData, setRowData] = useState();

  const [userInfo, setUserInfo] = useState(
    jwt_decode(sessionStorage.getItem("token"))
  );

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const response = await WhatsAppAPI.getInstagramSettingRecord();
      if (response.success) {
        setBody(response.record);
      } else {
        setBody([]);
      }
    } catch (error) {
      console.error("Error fetching Instagram settings:", error);
      setBody([]);
    } finally {
      setIsSpinner(true);
    }
  };

  const editRecord = (data) => {
    setModalShowHide(true);
    setRowData(data?.row);
  };

  const createRecord = () => {
    setModalShowHide(true);
    setRowData();
  };

  const deleteRecord = async (id) => {
    if (window.confirm("Are you sure you want to delete this setting?")) {
      try {
        const response = await WhatsAppAPI.deleteInstagramSettingRecord(id);
        if (response.success) {
          toast.success("Setting deleted successfully.");
          fetchData();
        } else {
          toast.error("Failed to delete setting.");
        }
      } catch (err) {
        toast.error("Error deleting setting.");
      }
    }
  };

  const onRefreshData = () => {
    setModalShowHide(false);
    fetchData();
  };

  const capitalize = (text) =>
    text ? text.charAt(0).toUpperCase() + text.slice(1) : "";

  const labels = { beforeSelect: " " };
  const header = [
    {
      title: "Name",
      prop: "name",
      isFilterable: true,
      cell: (row) => capitalize(row.name),
    },
    { title: "App ID", prop: "app_id" },
    { title: "Facebook Page ID", prop: "page_id", isFilterable: true },
    { title: "Default Business Number", prop: "business_number", isFilterable: true },
    ...(userInfo?.userrole === "SYS_ADMIN" || userInfo?.userrole === "ADMIN"
      ? [
          {
            title: "Actions",
            prop: "id",
            cell: (row) => (
              <div className="d-flex gap-2">
                <Button
                  variant="outline-primary"
                  className="btn-sm"
                  onClick={() => editRecord({ row })}
                  title="Edit Setting"
                >
                  <FaEdit />
                </Button>
                <Button
                  variant="outline-danger"
                  className="btn-sm"
                  onClick={() => deleteRecord(row.id)}
                  title="Delete Setting"
                >
                  <FaTrash />
                </Button>
              </div>
            ),
          },
        ]
      : []),
  ];

  return (
    <>
      <Container className="mt-5">
        <Row className="mx-5 mb-4">
          <Card className="p-4 header-bg shadow-sm border-0 rounded-3">
            <h5 className="mb-2 fw-bold">💡 Quick Guide</h5>
            <small>
              Here you can configure ads instant form lead capture. Whosoever fills the form, the CRM will have the leads.
            </small>
          </Card>
        </Row>

        <Row className="mx-5 g-0">
          <Col lg={12} className="mb-3">
            <Row className="g-0">
              <Col lg={12}>
                <div className="bg-white shadow-sm border-0 rounded-3 p-4">
                  <h5 className="fw-semibold mb-3">⚙️ Instant Form Capture Settings</h5>

                  {isSpinner ? (
                    <DatatableWrapper
                      body={body}
                      headers={header}
                      paginationOptionsProps={{
                        initialState: {
                          rowsPerPage: 10,
                          options: [5, 10, 15, 20],
                        },
                      }}
                    >
                      <Row className="mb-3">
                        <Col
                          lg={3}
                          sm={10}
                          xs={10}
                          className="d-flex flex-col justify-content-end align-items-end"
                        >
                          <Filter className="p-2" />
                        </Col>
                        <Col
                          lg={4}
                          sm={2}
                          xs={2}
                          className="d-flex flex-col justify-content-start align-items-start"
                        >
                          <PaginationOptions labels={labels} />
                        </Col>
                        <Col
                          lg={5}
                          sm={12}
                          xs={12}
                          className="mt-2 d-flex flex-col justify-content-end align-items-end gap-2"
                        >
                          <Button
                            className="btn btn-sm text-white"
                            style={{ backgroundColor: "#3b5998", border: "none" }}
                            onClick={() => navigate("/instagram_setting/form_mapping")}
                          >
                            📋 Instant Form Mappings
                          </Button>
                          <Button
                            className="btn btn-sm text-white"
                            style={{ backgroundColor: "#e1306c", border: "none" }}
                            onClick={() => createRecord()}
                          >
                            <FaPlus /> Add Instagram Setting
                          </Button>
                        </Col>
                      </Row>

                      <div className="table-responsive">
                        <Table
                          striped
                          bordered
                          className="related-list-table align-middle"
                          responsive="sm"
                        >
                          <TableHeader />
                          <TableBody />
                        </Table>
                      </div>
                      <Pagination />
                    </DatatableWrapper>
                  ) : (
                    <ShimmerTable row={10} col={4} />
                  )}
                </div>
              </Col>
            </Row>
          </Col>
        </Row>
      </Container>

      {modalShowHide && (
        <InstagramSettingEdit
          show={modalShowHide}
          onHide={() => setModalShowHide(false)}
          onRefreshData={onRefreshData}
          rowData={rowData}
        />
      )}

      <ToastContainer autoClose={3000} />
    </>
  );
};

export default InstagramSetting;
